/**
 * @fileoverview Hook de Validações em Tempo Real
 * 
 * @module useValuationValidations
 * @description
 * Valida premissas do valuation em tempo real conforme auditoria:
 * 
 * 1. **WACC > g (crescimento perpétuo)**
 *    - Crítico: WACC ≤ g invalida o modelo DCF (divisão por zero/negativo)
 *    - Ação: Bloqueio de cálculo
 * 
 * 2. **Hierarquia de Margens**
 *    - Regra: Margem Líquida ≤ Margem EBITDA ≤ Margem Bruta
 *    - Ação: Warning se hierarquia quebrada
 * 
 * 3. **Taxa de Crescimento Perpétuo**
 *    - Regra: g ≤ PIB + Inflação (benchmark macroeconômico)
 *    - Ação: Warning se g excessivo
 * 
 * @returns {ValidationResult[]} Array de validações com status e mensagens
 */

import { useMemo } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export type ValidationSeverity = 'error' | 'warning' | 'info' | 'success';

export interface ValidationResult {
  id: string;
  severity: ValidationSeverity;
  message: string;
  passed: boolean;
  details?: string;
  action?: string;
}

interface MargemData {
  bruta?: number;
  ebitda?: number;
  liquida?: number;
}

interface ValuationParams {
  wacc: number;
  g: number;
  pib?: number;
  inflacao?: number;
  margens?: MargemData;
}

// ============================================================================
// HOOK
// ============================================================================

export const useValuationValidations = (params: ValuationParams): ValidationResult[] => {
  return useMemo(() => {
    const validations: ValidationResult[] = [];

    // ========================================================================
    // VALIDAÇÃO 1: WACC > g (CRÍTICA)
    // ========================================================================
    const waccVsG = {
      id: 'wacc-vs-g',
      severity: (params.wacc <= params.g ? 'error' : 'success') as ValidationSeverity,
      passed: params.wacc > params.g,
      message:
        params.wacc <= params.g
          ? `⛔ CRÍTICO: WACC (${(params.wacc * 100).toFixed(2)}%) ≤ g (${(params.g * 100).toFixed(2)}%)`
          : `✅ WACC (${(params.wacc * 100).toFixed(2)}%) > g (${(params.g * 100).toFixed(2)}%)`,
      details:
        params.wacc <= params.g
          ? 'O WACC deve ser SEMPRE maior que a taxa de crescimento perpétuo (g). Caso contrário, a fórmula do Terminal Value resulta em divisão por zero ou valor negativo, invalidando o modelo DCF.'
          : 'WACC adequadamente maior que a taxa de crescimento perpétuo.',
      action:
        params.wacc <= params.g
          ? 'Ajuste o WACC (aumentar) ou reduza g. Sugestão: WACC mínimo = g + 3%'
          : undefined,
    };
    validations.push(waccVsG);

    // ========================================================================
    // VALIDAÇÃO 2: Hierarquia de Margens
    // ========================================================================
    if (params.margens && params.margens.bruta !== undefined) {
      const { bruta, ebitda, liquida } = params.margens;

      // Validar: Líquida ≤ EBITDA ≤ Bruta
      const hierarquiaOK =
        (liquida === undefined || ebitda === undefined || liquida <= ebitda) &&
        (ebitda === undefined || bruta === undefined || ebitda <= bruta) &&
        (liquida === undefined || bruta === undefined || liquida <= bruta);

      const hierarquiaMargens: ValidationResult = {
        id: 'hierarquia-margens',
        severity: hierarquiaOK ? 'success' : 'warning',
        passed: hierarquiaOK,
        message: hierarquiaOK
          ? '✅ Hierarquia de margens consistente'
          : '⚠️ Hierarquia de margens inconsistente',
        details: hierarquiaOK
          ? `Margem Bruta (${(bruta! * 100).toFixed(1)}%) ≥ EBITDA (${((ebitda || 0) * 100).toFixed(1)}%) ≥ Líquida (${((liquida || 0) * 100).toFixed(1)}%)`
          : `A hierarquia esperada é: Margem Bruta ≥ Margem EBITDA ≥ Margem Líquida. Valores atuais: Bruta ${(bruta! * 100).toFixed(1)}%, EBITDA ${((ebitda || 0) * 100).toFixed(1)}%, Líquida ${((liquida || 0) * 100).toFixed(1)}%.`,
        action: hierarquiaOK
          ? undefined
          : 'Revise as projeções de DRE. Margens invertidas podem indicar erro nas premissas.',
      };
      validations.push(hierarquiaMargens);
    }

    // ========================================================================
    // VALIDAÇÃO 3: g ≤ PIB + Inflação
    // ========================================================================
    if (params.pib !== undefined && params.inflacao !== undefined) {
      const crescimentoNominalMax = params.pib + params.inflacao;
      const gExcessivo = params.g > crescimentoNominalMax;

      const crescimentoVsPib: ValidationResult = {
        id: 'g-vs-pib',
        severity: gExcessivo ? 'warning' : 'success',
        passed: !gExcessivo,
        message: gExcessivo
          ? `⚠️ Taxa g (${(params.g * 100).toFixed(1)}%) acima do PIB + Inflação (${(crescimentoNominalMax * 100).toFixed(1)}%)`
          : `✅ Taxa g (${(params.g * 100).toFixed(1)}%) dentro do benchmark macroeconômico`,
        details: gExcessivo
          ? `A taxa de crescimento perpétuo (g) não deveria exceder o crescimento nominal da economia (PIB real + Inflação = ${(crescimentoNominalMax * 100).toFixed(1)}%). Taxas muito altas pressupõem que a empresa crescerá indefinidamente acima da economia.`
          : `Taxa de crescimento perpétuo consistente com projeções macroeconômicas (PIB ${(params.pib * 100).toFixed(1)}% + Inflação ${(params.inflacao * 100).toFixed(1)}%).`,
        action: gExcessivo
          ? `Considere reduzir g para no máximo ${(crescimentoNominalMax * 100).toFixed(1)}% ou justifique premissas excepcionais.`
          : undefined,
      };
      validations.push(crescimentoVsPib);
    }

    return validations;
  }, [params]);
};

/**
 * Retorna apenas validações com problemas (error ou warning)
 */
export const useProblematicValidations = (params: ValuationParams): ValidationResult[] => {
  const allValidations = useValuationValidations(params);
  return useMemo(
    () => allValidations.filter(v => v.severity === 'error' || v.severity === 'warning'),
    [allValidations]
  );
};

/**
 * Verifica se há validações críticas (error) que bloqueiam o cálculo
 */
export const useHasCriticalErrors = (params: ValuationParams): boolean => {
  const allValidations = useValuationValidations(params);
  return useMemo(
    () => allValidations.some(v => v.severity === 'error' && !v.passed),
    [allValidations]
  );
};

// ============================================================================
// VALIDAÇÕES INDIVIDUAIS (Sprint 1 - Final 12.5%)
// ============================================================================

/**
 * Valida valores de Beta (CAPM)
 * 
 * REGRAS:
 * - ERROR: Beta < 0 (impossível para ativos reais)
 * - WARNING: Beta > 3 (extremamente volátil, revisar)
 * - WARNING: Beta < 0.3 (extremamente defensivo, revisar)
 * - OK: 0.3 <= Beta <= 3
 * 
 * @example
 * validateBeta(-0.5)  → { isValid: false, error: "Beta não pode ser negativo", severity: 'error' }
 * validateBeta(4.0)   → { isValid: true, warning: "Beta muito alto (>3)...", severity: 'warning' }
 * validateBeta(1.2)   → { isValid: true }
 */
export interface SimpleValidationResult {
  isValid: boolean;
  error?: string;
  warning?: string;
  severity?: 'error' | 'warning' | 'info';
}

export function validateBeta(beta: number): SimpleValidationResult {
  // ERROR: Beta negativo
  if (beta < 0) {
    return {
      isValid: false,
      error: 'Beta não pode ser negativo. Valores típicos: 0.3 (defensivo) a 3.0 (muito volátil).',
      severity: 'error',
    };
  }

  // WARNING: Beta extremamente alto (>3)
  if (beta > 3) {
    return {
      isValid: true,
      warning: `Beta muito alto (${beta.toFixed(2)}). Valores acima de 3.0 indicam volatilidade extrema. Revisar se adequado ao setor.`,
      severity: 'warning',
    };
  }

  // WARNING: Beta extremamente baixo (<0.3)
  if (beta < 0.3) {
    return {
      isValid: true,
      warning: `Beta muito baixo (${beta.toFixed(2)}). Valores abaixo de 0.3 indicam ativo extremamente defensivo. Revisar se adequado ao setor.`,
      severity: 'warning',
    };
  }

  // OK: Beta dentro do range esperado (0.3 - 3.0)
  return { isValid: true };
}

/**
 * Valida margens (Bruta, EBITDA, Líquida)
 * 
 * REGRAS:
 * - ERROR: Margem < 0% (negativa)
 * - ERROR: Margem > 100% (impossível)
 * - WARNING: Margem < 5% (muito baixa, revisar viabilidade)
 * - WARNING: Margem > 95% (muito alta, revisar realismo)
 * - OK: 5% <= Margem <= 95%
 * 
 * @param margin - Valor da margem em decimal (0.10 = 10%)
 * @param type - Tipo de margem ('Bruta', 'EBITDA', 'Líquida')
 * @param year - Ano da projeção (opcional, para mensagem contextualizada)
 * 
 * @example
 * validateMargin(-0.10, 'EBITDA', 2025)  → { isValid: false, error: "Margem EBITDA 2025 negativa...", severity: 'error' }
 * validateMargin(0.02, 'Líquida', 2026)  → { isValid: true, warning: "Margem Líquida 2026 muito baixa...", severity: 'warning' }
 * validateMargin(0.25, 'Bruta', 2027)    → { isValid: true }
 */
export function validateMargin(
  margin: number,
  type: 'Bruta' | 'EBITDA' | 'Líquida',
  year?: number
): SimpleValidationResult {
  const yearLabel = year ? ` (${year})` : '';
  const marginPct = (margin * 100).toFixed(1);

  // ERROR: Margem negativa
  if (margin < 0) {
    return {
      isValid: false,
      error: `Margem ${type}${yearLabel} não pode ser negativa (${marginPct}%). Revisar estrutura de custos.`,
      severity: 'error',
    };
  }

  // ERROR: Margem > 100%
  if (margin > 1) {
    return {
      isValid: false,
      error: `Margem ${type}${yearLabel} não pode exceder 100% (valor atual: ${marginPct}%). Revisar cálculo.`,
      severity: 'error',
    };
  }

  // WARNING: Margem muito baixa (<5%)
  if (margin < 0.05) {
    return {
      isValid: true,
      warning: `Margem ${type}${yearLabel} muito baixa (${marginPct}%). Valores abaixo de 5% indicam baixa rentabilidade. Revisar viabilidade.`,
      severity: 'warning',
    };
  }

  // WARNING: Margem muito alta (>95%)
  if (margin > 0.95) {
    return {
      isValid: true,
      warning: `Margem ${type}${yearLabel} muito alta (${marginPct}%). Valores acima de 95% são raros. Revisar realismo das projeções.`,
      severity: 'warning',
    };
  }

  // OK: Margem dentro do range esperado (5% - 95%)
  return { isValid: true };
}

/**
 * Valida percentual de CAPEX em relação à Receita
 * 
 * REGRAS:
 * - ERROR: CAPEX < 0% (negativo)
 * - WARNING: CAPEX > 50% (insustentável a longo prazo)
 * - INFO: CAPEX > 30% (alto, mas aceitável em fases de expansão)
 * - OK: 0% <= CAPEX <= 30%
 * 
 * @param capexPct - CAPEX % da Receita em decimal (0.15 = 15%)
 * @param year - Ano da projeção (opcional, para mensagem contextualizada)
 * 
 * @example
 * validateCapex(-0.05, 2025)  → { isValid: false, error: "CAPEX 2025 negativo...", severity: 'error' }
 * validateCapex(0.80, 2026)   → { isValid: true, warning: "CAPEX 2026 muito alto (80%)...", severity: 'warning' }
 * validateCapex(0.15, 2027)   → { isValid: true }
 */
export function validateCapex(capexPct: number, year?: number): SimpleValidationResult {
  const yearLabel = year ? ` (${year})` : '';
  const capexPctDisplay = (capexPct * 100).toFixed(1);

  // ERROR: CAPEX negativo
  if (capexPct < 0) {
    return {
      isValid: false,
      error: `CAPEX${yearLabel} não pode ser negativo (${capexPctDisplay}%). Revisar projeção de investimentos.`,
      severity: 'error',
    };
  }

  // WARNING: CAPEX muito alto (>50%)
  if (capexPct > 0.5) {
    return {
      isValid: true,
      warning: `CAPEX${yearLabel} muito alto (${capexPctDisplay}%). Valores acima de 50% da receita são insustentáveis a longo prazo. Revisar estratégia de investimentos.`,
      severity: 'warning',
    };
  }

  // INFO: CAPEX alto (>30%), mas aceitável em expansão
  if (capexPct > 0.3) {
    return {
      isValid: true,
      warning: `CAPEX${yearLabel} alto (${capexPctDisplay}%). Aceitável em fases de expansão, mas monitorar sustentabilidade.`,
      severity: 'info',
    };
  }

  // OK: CAPEX dentro do range esperado (0% - 30%)
  return { isValid: true };
}
